/**
 * Uniform read-model for phone state across all tabs.
 *
 *  - Owner: `client.ts` calls `publishOwnerSnapshot()` on every change; we store
 *    it, notify local subscribers, and broadcast it to followers over the bus.
 *  - Follower: we receive snapshots over the bus and serve them to local
 *    subscribers. Followers never touch the registry or the SDK.
 *
 * Consumers (`useTelephonyClient`, `AppLayout`) read here and don't care which
 * role the tab plays — that's the whole point of this layer.
 */

import * as phoneBus from './phone-bus';
import type { CallError, PhoneSnapshot, SerializableCall, SerializableStats } from './phone-bus';
import { isLeader } from './leader';
import type { CallRecord, PhoneStatus } from './types';

export interface LiveSnapshot {
  calls: CallRecord[];
  status: PhoneStatus;
  muted: boolean;
  disposition: CallRecord | null;
  stats: SerializableStats | null;
  lastError: CallError | null;
  notice: string | null;
}

function emptySnapshot(): LiveSnapshot {
  return {
    calls: [],
    status: 'disconnected',
    muted: false,
    disposition: null,
    stats: null,
    lastError: null,
    notice: null,
  };
}

let current: LiveSnapshot = emptySnapshot();

type Listener = () => void;
const listeners = new Set<Listener>();
let wired = false;

function notify(): void {
  listeners.forEach((l) => {
    try {
      l();
    } catch (err) {
      console.error('[telephony/phone-state] listener error', err);
    }
  });
}

function serializeCall(r: CallRecord): SerializableCall {
  return {
    clientCallId: r.clientCallId,
    callId: r.callId,
    telnyxCallId: r.telnyxCallId,
    conversationId: r.conversationId,
    direction: r.direction,
    state: r.state,
    brand: r.brand,
    remoteNumber: r.remoteNumber,
    startedAt: r.startedAt.toISOString(),
    answeredAt: r.answeredAt ? r.answeredAt.toISOString() : null,
    endedAt: r.endedAt ? r.endedAt.toISOString() : null,
    customer: r.customer,
    recoveredFromCallId: r.recoveredFromCallId,
    holdSince: r.holdSince ? r.holdSince.toISOString() : null,
    transferState: r.transferState,
  };
}

function deserializeCall(c: SerializableCall): CallRecord {
  return {
    clientCallId: c.clientCallId,
    callId: c.callId,
    telnyxCallId: c.telnyxCallId,
    conversationId: c.conversationId,
    direction: c.direction,
    state: c.state,
    brand: c.brand,
    remoteNumber: c.remoteNumber,
    startedAt: new Date(c.startedAt),
    answeredAt: c.answeredAt ? new Date(c.answeredAt) : null,
    endedAt: c.endedAt ? new Date(c.endedAt) : null,
    customer: c.customer,
    recoveredFromCallId: c.recoveredFromCallId,
    holdSince: c.holdSince ? new Date(c.holdSince) : null,
    transferState: c.transferState ?? null,
  };
}

function toSnapshot(live: LiveSnapshot): PhoneSnapshot {
  return {
    calls: live.calls.map(serializeCall),
    status: live.status,
    muted: live.muted,
    disposition: live.disposition ? serializeCall(live.disposition) : null,
    stats: live.stats,
    lastError: live.lastError,
    notice: live.notice,
  };
}

function fromSnapshot(snap: PhoneSnapshot): LiveSnapshot {
  return {
    calls: snap.calls.map(deserializeCall),
    status: snap.status,
    muted: snap.muted,
    disposition: snap.disposition ? deserializeCall(snap.disposition) : null,
    stats: snap.stats,
    lastError: snap.lastError ?? null,
    notice: snap.notice ?? null,
  };
}

/**
 * Wire the bus listeners. Called from `initTelephony` on every tab; safe to
 * call repeatedly.
 */
export function initPhoneState(): void {
  if (wired) return;
  wired = true;
  phoneBus.onMessage((msg) => {
    if (msg.kind === 'snapshot') {
      // Only followers apply received snapshots; the owner is authoritative
      // and would otherwise clobber its own state with an echoed message.
      if (isLeader()) return;
      current = fromSnapshot(msg.snapshot);
      notify();
    } else if (msg.kind === 'request-snapshot') {
      // A new follower asked for an immediate sync; only the owner answers.
      if (isLeader()) phoneBus.postSnapshot(toSnapshot(current));
    }
  });
}

/** Owner-only: store + broadcast a fresh snapshot built from the registry. */
export function publishOwnerSnapshot(live: LiveSnapshot): void {
  current = live;
  notify();
  phoneBus.postSnapshot(toSnapshot(live));
}

/** Follower-only: ask the owner for an immediate snapshot (e.g. on init). */
export function requestSync(): void {
  phoneBus.requestSnapshot();
}

/**
 * Reset the local mirror to a disconnected baseline (on resign/dispose).
 * Local-only: does NOT broadcast — a resigning tab must not clobber the new
 * owner's snapshot.
 */
export function resetState(): void {
  current = emptySnapshot();
  notify();
}

export function getCalls(): CallRecord[] {
  return current.calls;
}

export function getStatus(): PhoneStatus {
  return current.status;
}

export function getMuted(): boolean {
  return current.muted;
}

export function getDisposition(): CallRecord | null {
  return current.disposition;
}

export function getStats(): SerializableStats | null {
  return current.stats;
}

export function getLastError(): CallError | null {
  return current.lastError;
}

export function getNotice(): string | null {
  return current.notice;
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
